import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — scroll-triggered fade-up entrance.
 *
 * - Lightweight: pure IntersectionObserver, no deps.
 * - Plays once. Once in view, the observer disconnects.
 * - Respects prefers-reduced-motion (renders fully visible immediately).
 * - Mobile uses a softer translate via CSS media query in index.css.
 *
 * Props:
 *   delay   ms — stagger offset (e.g. 0, 80, 160 for cards in a row)
 *   amount  IntersectionObserver threshold (default 0.12)
 *   as      element tag (default 'div')
 */
export default function Reveal({
  children,
  delay = 0,
  amount = 0.12,
  as: Tag = "div",
  className = "",
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: amount, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [amount]);

  return (
    <Tag
      ref={ref}
      className={`aw-reveal ${shown ? "aw-reveal-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}>
      {children}
    </Tag>
  );
}
